/**
 * Reading an inventory back off disk.
 *
 * A save holds ids and counts as plain strings and numbers, and a build that has since renamed an
 * item or dropped a slot has no way to know that from the types. `StoredInventory` is the shape as
 * it comes back, and `migrateInventory` is the one gate between it and `InventoryState`: version
 * first, then every id, and a typed failure rather than a half-loaded character.
 *
 * There is still no migration chain. Version 2 added the roll a weapon was generated with, and
 * nothing has been persisted at version 1, so the seam stays empty rather than furnished until a
 * save exists to migrate. See src/save/AGENTS.md.
 */

import { EMPTY_EQUIPMENT, INVENTORY_SCHEMA_VERSION } from '@/sim/inventory/inventoryContract';
import { fail, succeed } from '@/sim/inventory/inventoryResult';
import { isSlotId } from '@/sim/inventory/slotRegistry';
import { readRolledWeapon } from '@/sim/inventory/storedWeaponRoll';
import { isStackable } from '@/sim/items/itemContract';
import { isItemId, itemDef } from '@/sim/items/itemRegistry';
import type {
  Container,
  Equipment,
  InventoryState,
  ItemStack,
} from '@/sim/inventory/inventoryContract';
import type { InventoryResult } from '@/sim/inventory/inventoryResult';
import type { StoredRolledWeapon } from '@/sim/inventory/storedWeaponRoll';

/** One stack as a save holds it: an id nobody has checked, and a count nobody has bounded. */
export interface StoredStack {
  readonly defId: string;
  readonly count: number;
  readonly instanceId: number | null;
  readonly rolled: StoredRolledWeapon | null;
}

export interface StoredContainer {
  readonly capacityKg: number;
  readonly stacks: readonly StoredStack[];
}

export interface StoredInventory {
  readonly version: number;
  readonly carried: StoredContainer;
  readonly stash: StoredContainer;
  readonly equipment: Readonly<Record<string, StoredStack | null>>;
  readonly nextInstanceId: number;
}

/**
 * Validates a stored inventory into a usable one.
 *
 * Rejects rather than repairs. A save one version behind is a migration nobody has written yet, and
 * silently dropping the two items that no longer exist is how a player loses a plate carrier and
 * blames the netcode.
 */
export function migrateInventory(stored: StoredInventory): InventoryResult<InventoryState> {
  if (stored.version !== INVENTORY_SCHEMA_VERSION) {
    return fail({
      reason: 'versionMismatch',
      found: stored.version,
      expected: INVENTORY_SCHEMA_VERSION,
    });
  }

  const carried = readContainer(stored.carried);
  if (!carried.ok) return fail(carried.failure);

  const stash = readContainer(stored.stash);
  if (!stash.ok) return fail(stash.failure);

  const equipment = readEquipment(stored.equipment);
  if (!equipment.ok) return fail(equipment.failure);

  return succeed({
    version: INVENTORY_SCHEMA_VERSION,
    carried: carried.value,
    stash: stash.value,
    equipment: equipment.value,
    nextInstanceId: Math.max(1, Math.floor(stored.nextInstanceId)),
  });
}

function readContainer(stored: StoredContainer): InventoryResult<Container> {
  const stacks: ItemStack[] = [];

  for (const entry of stored.stacks) {
    const stack = readStack(entry);
    if (!stack.ok) return fail(stack.failure);
    stacks.push(stack.value);
  }

  return succeed({ capacityKg: Math.max(0, stored.capacityKg), stacks });
}

function readEquipment(
  stored: Readonly<Record<string, StoredStack | null>>,
): InventoryResult<Equipment> {
  const equipment: Record<string, ItemStack | null> = { ...EMPTY_EQUIPMENT };

  for (const [slot, entry] of Object.entries(stored)) {
    if (!isSlotId(slot)) return fail({ reason: 'unknownSlot', slot });
    if (entry === null) continue;

    const stack = readStack(entry);
    if (!stack.ok) return fail(stack.failure);
    equipment[slot] = stack.value;
  }

  return succeed({ ...EMPTY_EQUIPMENT, ...equipment });
}

/** One stack, with its count pulled back inside what the def actually allows. */
function readStack(stored: StoredStack): InventoryResult<ItemStack> {
  if (!isItemId(stored.defId)) return fail({ reason: 'unknownItem', defId: stored.defId });

  const def = itemDef(stored.defId);
  const count = Math.floor(stored.count);
  if (!Number.isFinite(count) || count < 1 || count > def.stackLimit) {
    return fail({ reason: 'invalidCount', count: stored.count });
  }

  const rolled = readRolledWeapon(stored.rolled, def);
  if (!rolled.ok) return fail(rolled.failure);

  return succeed({
    defId: def.id,
    count,
    instanceId: isStackable(def) ? null : stored.instanceId,
    rolled: rolled.value,
  });
}
